// Shared UI primitives for the Audiaro dark design (from Audiaro.dc.html).

import { useMemo } from 'react';

/* ---------------------------------- Icons --------------------------------- */

// Stroke path sets from the design comp.
export const ICON_PATHS = {
  dashboard: ['M3 3h8v8H3z', 'M13 3h8v5h-8z', 'M13 12h8v9h-8z', 'M3 16h8v5H3z'],
  generate: ['M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z', 'M5 10a7 7 0 0 0 14 0', 'M12 17v4', 'M8 21h8'],
  library: ['M3 10v4', 'M8 6v12', 'M13 3v18', 'M18 7v10', 'M21 10v4'],
  book: ['M4 19.5A2.5 2.5 0 0 1 6.5 17H20', 'M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z'],
  billing: ['M2 6h20v12H2z', 'M2 10h20', 'M6 15h4'],
  settings: ['M4 6h16', 'M4 12h16', 'M4 18h16', 'M14 6a2 2 0 1 0 .001 0', 'M8 12a2 2 0 1 0 .001 0', 'M16 18a2 2 0 1 0 .001 0'],
  admin: ['M12 2l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V5z', 'M9 12l2 2 4-4'],
  users: ['M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2', 'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', 'M22 21v-2a4 4 0 0 0-3-3.87', 'M16 3.13a4 4 0 0 1 0 7.75'],
  key: ['M21 2l-9.6 9.6', 'M15.5 7.5l3 3L22 7l-3-3', 'M7.5 12.5a5 5 0 1 0 4 4'],
  tiers: ['M12 2l9 5-9 5-9-5z', 'M3 12l9 5 9-5', 'M3 17l9 5 9-5'],
  spark: ['M12 3l1.9 5L19 10l-5.1 2L12 17l-1.9-5L5 10l5.1-2z'],
  search: ['M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14z', 'M20 20l-4-4'],
  plus: ['M12 5v14', 'M5 12h14'],
  download: ['M12 3v12', 'M7 12l5 5 5-5', 'M5 21h14'],
  logout: ['M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4', 'M16 17l5-5-5-5', 'M21 12H9'],
  check: ['M5 12l4 4L19 7'],
  play: ['M7 4l13 8-13 8z'],
  pause: ['M8 5h3v14H8z', 'M13 5h3v14h-3z'],
  trash: ['M3 6h18', 'M8 6V4h8v2', 'M19 6l-1 14H6L5 6', 'M10 11v6', 'M14 11v6'],
  copy: ['M9 9h11v11H9z', 'M5 15H4V4h11v1'],
  eye: ['M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z', 'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z'],
  refresh: ['M21 12a9 9 0 1 1-2.64-6.36', 'M21 3v6h-6'],
  pencil: ['M12 20h9', 'M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z'],
  chevronUp: ['M6 15l6-6 6 6'],
  chevronDown: ['M6 9l6 6 6-6'],
  mic: ['M12 2a3 3 0 0 1 3 3v6a3 3 0 0 1-6 0V5a3 3 0 0 1 3-3z', 'M19 10v1a7 7 0 0 1-14 0v-1', 'M12 18v4'],
} as const;

export type IconName = keyof typeof ICON_PATHS;

export function Icon({
  name,
  size = 20,
  fill,
  className,
}: {
  name: IconName;
  size?: number;
  fill?: string;
  className?: string;
}) {
  const paths = ICON_PATHS[name];
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill || 'none'}
      stroke={fill ? 'none' : 'currentColor'}
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {paths.map((d, i) => (
        <path key={i} d={d} />
      ))}
    </svg>
  );
}

/* -------------------------------- Waveform -------------------------------- */

// Deterministic decorative waveform seeded by an id/title, as in the design comp.
export function Waveform({ seed, active = false, big = false }: { seed: string; active?: boolean; big?: boolean }) {
  const bars = useMemo(() => {
    const n = big ? 60 : 26;
    const H = big ? 46 : 26;
    let s = 7;
    for (const ch of seed || 'x') s = (s * 31 + ch.charCodeAt(0)) % 9973;
    return Array.from({ length: n }, (_, i) => {
      const v = Math.sin(i * 0.55 + s) * 0.5 + 0.5;
      const v2 = Math.sin(i * 1.73 + s * 0.3) * 0.5 + 0.5;
      const hh = Math.max(0.12, Math.min(1, 0.16 + 0.84 * (v * 0.55 + v2 * 0.45)));
      return Math.round(hh * H);
    });
  }, [seed, big]);

  const H = big ? 46 : 26;
  return (
    <div className="flex items-center gap-[2px] overflow-hidden" style={{ height: H }}>
      {bars.map((h, i) => (
        <span
          key={i}
          className="block w-[3px] flex-none rounded-[3px]"
          style={{
            height: h,
            background: active ? 'linear-gradient(180deg,var(--a-hi),var(--a-lo))' : '#34343B',
          }}
        />
      ))}
    </div>
  );
}

/* ------------------------------- StatusBadge ------------------------------ */

const STATUS_STYLES: Record<string, { label: string; cls: string }> = {
  completed: { label: 'Completed', cls: 'badge-success' },
  active: { label: 'Active', cls: 'badge-success' },
  valid: { label: 'Valid', cls: 'badge-success' },
  operational: { label: 'Operational', cls: 'badge-success' },
  processing: { label: 'Processing', cls: 'badge-info' },
  queued: { label: 'Queued', cls: 'badge-info' },
  pending: { label: 'Pending', cls: 'badge-warning' },
  trialing: { label: 'Trialing', cls: 'badge-info' },
  failed: { label: 'Failed', cls: 'badge-danger' },
  invalid: { label: 'Invalid', cls: 'badge-danger' },
  canceled: { label: 'Canceled', cls: 'badge-danger' },
  past_due: { label: 'Past due', cls: 'badge-danger' },
  paused: { label: 'Paused', cls: 'badge-gray' },
  inactive: { label: 'Inactive', cls: 'badge-gray' },
  draft: { label: 'Draft', cls: 'badge-gray' },
  stitching: { label: 'Stitching', cls: 'badge-info' },
  ready: { label: 'Ready', cls: 'badge-success' },
};

export function StatusBadge({ status, label }: { status: string; label?: string }) {
  const s = STATUS_STYLES[status] || { label: status, cls: 'badge-gray' };
  return (
    <span className={`badge ${s.cls}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {label || s.label}
    </span>
  );
}

/* --------------------------------- Switch --------------------------------- */

export function Switch({ checked, onChange }: { checked: boolean; onChange?: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      className="relative flex-none rounded-full transition-colors duration-200"
      style={{
        width: 46,
        height: 26,
        background: checked ? 'linear-gradient(135deg,var(--a-hi),var(--a-lo))' : '#2C2C33',
        border: '1px solid ' + (checked ? 'transparent' : '#34343B'),
      }}
    >
      <span
        className="absolute top-[2px] rounded-full bg-white transition-all duration-200"
        style={{ width: 20, height: 20, left: checked ? 22 : 2 }}
      />
    </button>
  );
}

/* --------------------------------- Avatar --------------------------------- */

export function Avatar({ text, size = 36, className = '' }: { text: string; size?: number; className?: string }) {
  return (
    <span
      className={`grad-accent-simple flex-none flex items-center justify-center rounded-full font-display font-extrabold text-ink-950 ${className}`}
      style={{ width: size, height: size, fontSize: Math.max(11, Math.round(size * 0.36)) }}
    >
      {text}
    </span>
  );
}

/* -------------------------------- Spinner --------------------------------- */

export function Spinner({ size = 18 }: { size?: number }) {
  return (
    <span
      className="block rounded-full animate-spin"
      style={{
        width: size,
        height: size,
        border: '2px solid #34343B',
        borderTopColor: 'var(--a-hi)',
      }}
    />
  );
}

/* ------------------------------ SectionTitle ------------------------------ */

export function CardTitle({ children }: { children: React.ReactNode }) {
  return <h3 className="font-display font-extrabold text-white text-[17px] m-0">{children}</h3>;
}
